import type {ErrorObject} from '@wix/dm-ajv/dist/2020'
import _ from 'lodash'

/**
 * Removes unevaluated properties from an object based on Ajv validation errors.
 * This is useful when working with JSON Schema validation that has `unevaluatedProperties: false`.
 *
 * @param data - The object to remove unevaluated properties from
 * @param errors - Optional array of Ajv validation errors. If not provided, no properties will be removed.
 * @returns The modified object with unevaluated properties removed
 */
export function removeUnevaluated(data: Record<string, any>, errors?: ErrorObject[]): Record<string, any> {
    if (!errors?.length) return data

    const normalizePath = (path: string) => path.replace(/^\//, '').replace(/\//g, '.')

    const unevaluatedToIgnore = errors.reduce((predicates, {instancePath, schemaPath, message}) => {
        if (message === 'must match a schema in anyOf') {
            predicates.push(
                _.matches({
                    instancePath,
                    schemaPath: schemaPath.replace(/\/anyOf$/, '/unevaluatedProperties'),
                    keyword: 'unevaluatedProperties'
                })
            )
        }
        return predicates
    }, [] as any[])

    const shouldIgnoreError = (err: ErrorObject) => _.some(unevaluatedToIgnore, matcher => matcher(err))

    const pathsToRemove = errors
        .filter(
            error =>
                (error.keyword === 'unevaluatedProperties' || error.schemaPath?.includes('/unevaluatedProperties/')) &&
                !shouldIgnoreError(error)
        )
        .map(error => {
            if (error.keyword === 'unevaluatedProperties' && error.params?.unevaluatedProperty) {
                const parentPath = normalizePath(error.instancePath || '')
                return parentPath
                    ? `${parentPath}.${error.params.unevaluatedProperty}`
                    : error.params.unevaluatedProperty
            }
            return normalizePath(error.instancePath || '')
        })
        .filter(Boolean)

    pathsToRemove.forEach(path => _.unset(data, path))
    return data
}
